import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ArrowLeft, Cookie, Settings, Eye, BarChart, Shield } from "lucide-react";
import { APP_CONFIG, CONTACT_INFO } from "@/utils/constants/app";

interface CookiePolicyPageProps {
  onBackToHome: () => void;
}

export function CookiePolicyPage({ onBackToHome }: CookiePolicyPageProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-orange-600 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              onClick={onBackToHome}
              className="text-white hover:bg-orange-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Torna alla Home
            </Button>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Cookie className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-2">
                  Cookie Policy
                </h1>
                <p className="text-xl opacity-90">
                  Informativa sull&apos;Utilizzo dei Cookie
                </p>
              </div>
            </div>
            <p className="text-lg opacity-80 max-w-3xl mx-auto">
              Questa pagina descrive come {APP_CONFIG.COMPANY_NAME} utilizza i cookie e tecnologie simili sul proprio sito web
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          
          {/* Ultima modifica */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-orange-800">
              <strong>Ultimo aggiornamento:</strong> 12 Agosto 2025
            </p>
          </div>

          <div className="space-y-8">
            
            {/* Cosa sono i Cookie */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Cookie className="w-6 h-6 mr-3 text-orange-600" />
                  1. Cosa sono i Cookie
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo quando visiti un sito web. 
                  Ci aiutano a fornire un servizio migliore e personalizzato.
                </p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Settings className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-blue-800 mb-2">Funzionalità</h4>
                    <p className="text-blue-700 text-sm">Migliorano l&apos;esperienza di navigazione</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-green-800 mb-2">Tracciamento</h4>
                    <p className="text-green-700 text-sm">Ci aiutano a capire come usi il sito</p>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <BarChart className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-purple-800 mb-2">Analisi</h4>
                    <p className="text-purple-700 text-sm">Statistiche per migliorare il servizio</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tipi di Cookie */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">2. Tipologie di Cookie Utilizzati</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Cookie Tecnici */}
                <div className="border-l-4 border-green-500 pl-6">
                  <div className="flex items-center mb-3">
                    <Shield className="w-5 h-5 text-green-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Cookie Tecnici Necessari</h4>
                    <span className="ml-auto bg-green-100 text-green-800 px-2 py-1 rounded text-xs">SEMPRE ATTIVI</span>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm leading-relaxed">
                    Essenziali per il funzionamento del sito. Non richiedono il consenso dell&apos;utente.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="p-2 text-left">Cookie</th>
                          <th className="p-2 text-left">Finalità</th>
                          <th className="p-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">session_id</td>
                          <td className="p-2">Gestione sessione utente</td>
                          <td className="p-2">Sessione</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">csrf_token</td>
                          <td className="p-2">Sicurezza moduli</td>
                          <td className="p-2">Sessione</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">cookie_consent</td>
                          <td className="p-2">Memorizza preferenze cookie</td>
                          <td className="p-2">1 anno</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Cookie Analitici */}
                <div className="border-l-4 border-blue-500 pl-6">
                  <div className="flex items-center mb-3">
                    <BarChart className="w-5 h-5 text-blue-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Cookie Analitici</h4>
                    <span className="ml-auto bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">CONSENSO RICHIESTO</span>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm leading-relaxed">
                    Raccolgono informazioni anonime su come i visitatori utilizzano il sito.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="p-2 text-left">Cookie</th>
                          <th className="p-2 text-left">Provider</th>
                          <th className="p-2 text-left">Finalità</th>
                          <th className="p-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">_ga</td>
                          <td className="p-2">Google Analytics</td>
                          <td className="p-2">Statistiche visite</td>
                          <td className="p-2">2 anni</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">_ga_*</td>
                          <td className="p-2">Google Analytics</td>
                          <td className="p-2">Statistiche avanzate</td>
                          <td className="p-2">2 anni</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">_gid</td>
                          <td className="p-2">Google Analytics</td>
                          <td className="p-2">Identificazione utente</td>
                          <td className="p-2">24 ore</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Cookie Funzionali */}
                <div className="border-l-4 border-purple-500 pl-6">
                  <div className="flex items-center mb-3">
                    <Settings className="w-5 h-5 text-purple-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Cookie Funzionali</h4>
                    <span className="ml-auto bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">CONSENSO RICHIESTO</span>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm leading-relaxed">
                    Migliorano la funzionalità del sito e permettono di ricordare le tue preferenze.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="p-2 text-left">Cookie</th>
                          <th className="p-2 text-left">Finalità</th>
                          <th className="p-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">user_preferences</td>
                          <td className="p-2">Preferenze utente</td>
                          <td className="p-2">6 mesi</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-mono text-xs">form_data</td>
                          <td className="p-2">Salvataggio moduli</td>
                          <td className="p-2">7 giorni</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

              </CardContent>
            </Card>

            {/* Gestione Cookie */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">3. Come Gestire i Cookie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Gestione tramite Browser</h4>
                    <p className="text-gray-700 text-sm leading-relaxed mb-4">
                      Puoi controllare e/o eliminare i cookie come desideri. Puoi eliminare tutti i cookie 
                      che sono già sul tuo computer e impostare la maggior parte dei browser per impedire 
                      che vengano memorizzati.
                    </p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">Chrome</span>
                        <span className="text-xs text-gray-600">Impostazioni → Privacy</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">Firefox</span>
                        <span className="text-xs text-gray-600">Preferenze → Privacy</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">Safari</span>
                        <span className="text-xs text-gray-600">Preferenze → Privacy</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">Edge</span>
                        <span className="text-xs text-gray-600">Impostazioni → Privacy</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Gestione Preferenze</h4>
                    <div className="space-y-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h5 className="font-semibold text-green-800 mb-2">Cookie Tecnici</h5>
                        <p className="text-green-700 text-sm mb-2">Sempre attivi (necessari)</p>
                        <div className="flex items-center">
                          <div className="w-10 h-6 bg-green-600 rounded-full p-1">
                            <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                          </div>
                          <span className="ml-2 text-sm text-green-600">ON</span>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h5 className="font-semibold text-blue-800 mb-2">Cookie Analitici</h5>
                        <p className="text-blue-700 text-sm mb-2">Statistiche e miglioramento</p>
                        <div className="flex items-center">
                          <div className="w-10 h-6 bg-gray-300 rounded-full p-1">
                            <div className="w-4 h-4 bg-white rounded-full"></div>
                          </div>
                          <span className="ml-2 text-sm text-gray-600">Personalizzabile</span>
                        </div>
                      </div>
                      
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h5 className="font-semibold text-purple-800 mb-2">Cookie Funzionali</h5>
                        <p className="text-purple-700 text-sm mb-2">Personalizzazione esperienza</p>
                        <div className="flex items-center">
                          <div className="w-10 h-6 bg-gray-300 rounded-full p-1">
                            <div className="w-4 h-4 bg-white rounded-full"></div>
                          </div>
                          <span className="ml-2 text-sm text-gray-600">Personalizzabile</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cookie di Terze Parti */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">4. Cookie di Terze Parti</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Il nostro sito utilizza anche cookie di terze parti per servizi esterni:
                </p>
                
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">Google Analytics</h4>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">ANALYTICS</span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">
                      Servizio di analisi web che ci aiuta a comprendere come i visitatori utilizzano il nostro sito.
                    </p>
                    <div className="flex items-center justify-between">
                      <a 
                        href="https://policies.google.com/privacy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm underline"
                      >
                        Privacy Policy Google
                      </a>
                      <a 
                        href="https://tools.google.com/dlpage/gaoptout" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm underline"
                      >
                        Opt-out Google Analytics
                      </a>
                    </div>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">Mappe e Servizi di Localizzazione</h4>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">FUNCTIONAL</span>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Per mostrare mappe interattive e servizi di localizzazione nella pagina contatti.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Diritti e Controllo */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">5. I Tuoi Diritti</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Cosa Puoi Fare</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                        <span className="text-sm text-gray-700">Accettare o rifiutare i cookie non tecnici</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                        <span className="text-sm text-gray-700">Modificare le tue preferenze in qualsiasi momento</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                        <span className="text-sm text-gray-700">Eliminare i cookie dal tuo browser</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                        <span className="text-sm text-gray-700">Disabilitare completamente i cookie</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Conseguenze del Rifiuto</h4>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800 text-sm leading-relaxed">
                        <strong>Attenzione:</strong> Disabilitando i cookie potresti limitare alcune funzionalità del sito, 
                        come la memorizzazione delle preferenze o il corretto funzionamento dei moduli di contatto.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Aggiornamenti */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">6. Aggiornamenti della Policy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Questa Cookie Policy può essere aggiornata periodicamente per riflettere modifiche 
                  nelle nostre pratiche sui cookie o per altri motivi operativi, legali o normativi.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 text-sm">
                    Ti consigliamo di rivedere questa pagina periodicamente per rimanere informato 
                    sui cookie che utilizziamo e su come li utilizziamo.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contatti */}
            <Card className="border-0 shadow-lg bg-orange-50">
              <CardHeader>
                <CardTitle className="text-2xl text-orange-800">7. Contatti</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-orange-700 leading-relaxed mb-4">
                  Per domande sui cookie o per esercitare i tuoi diritti, contattaci:
                </p>
                <div className="bg-white p-4 rounded-lg border border-orange-200">
                  <p><strong>Email:</strong> {CONTACT_INFO.PRIMARY_EMAIL}</p>
                  <p><strong>Telefono:</strong> {CONTACT_INFO.MAIN_PHONE}</p>
                  <p><strong>Indirizzo:</strong> {CONTACT_INFO.FULL_ADDRESS}</p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
    </div>
  );
}